import React, { useEffect, useState, useRef } from "react";
import { apiRequest } from "../../api/client";
import { EyeIcon, EyeOffIcon } from "./PasswordEyeIcons";

function OtpInputs({ value, onChange }) {
  const inputsRef = useRef([]);
  const digits = Array.from({ length: 4 }, (_, i) => (value || "").charAt(i) || "");

  function setAt(idx, ch) {
    const cleaned = (ch || "").replace(/\D/g, "").slice(0, 1);
    const next = [...digits];
    next[idx] = cleaned;
    onChange(next.join("").replace(/\D/g, "").slice(0, 4));
  }

  return (
    <div className="flex justify-center gap-2">
      {digits.map((d, idx) => (
        <input
          key={idx}
          ref={(el) => (inputsRef.current[idx] = el)}
          value={d}
          inputMode="numeric"
          autoComplete="one-time-code"
          onChange={(e) => {
            const ch = e.target.value;
            setAt(idx, ch);
            if (ch && idx < 3) inputsRef.current[idx + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !digits[idx] && idx > 0) {
              inputsRef.current[idx - 1]?.focus();
            }
            if (e.key === "ArrowLeft" && idx > 0) inputsRef.current[idx - 1]?.focus();
            if (e.key === "ArrowRight" && idx < 3) inputsRef.current[idx + 1]?.focus();
          }}
          className="h-12 w-10 rounded-xl border border-[#E6EAF0] bg-white text-center text-[18px] font-semibold text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
        />
      ))}
    </div>
  );
}

export default function ForgotPasswordOverlay({ open, onClose, onOpenLogin }) {
  const [step, setStep] = useState("email"); // email | otp | reset | success
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setStep("email");
    setEmail("");
    setOtp("");
    setPassword("");
    setPasswordConfirmation("");
    setToken(null);
    setError("");
    setFieldErrors({});
  }, [open]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    if (!open) return undefined;
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function handleSendOtp(e) {
    e.preventDefault();
    setError("");
    setFieldErrors({});
    if (!email?.trim()) {
      setFieldErrors({ email: "Email is required." });
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setFieldErrors({ email: "Enter a valid email address." });
      return;
    }
    setLoading(true);
    try {
      const res = await apiRequest("api/forgot_password/send_otp", {
        method: "POST",
        body: { email: email.trim() },
        token: null,
      });
      if (res.ok && res.data?.token) {
        setToken(res.data.token);
        setStep("otp");
        setOtp("");
      } else {
        setError(
          Array.isArray(res.data?.errors)
            ? res.data.errors.join("\n")
            : res.data?.message || "Email not found. Please try again."
        );
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp(e) {
    e.preventDefault();
    setError("");
    const cleaned = (otp || "").replace(/\D/g, "");
    if (cleaned.length !== 4) {
      setError("Please enter the 4 digit OTP.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiRequest("api/forgot_password/verify_otp", {
        method: "POST",
        body: { otp: cleaned },
        token,
      });
      if (res.ok) {
        setStep("reset");
        setPassword("");
        setPasswordConfirmation("");
      } else {
        setError(
          Array.isArray(res.data?.errors)
            ? res.data.errors.join("\n")
            : "Invalid or expired OTP. Please try again."
        );
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    setError("");
    setFieldErrors({});
    const nextErrors = {};
    if (!password) nextErrors.password = "Password is required.";
    if (!passwordConfirmation) {
      nextErrors.passwordConfirmation = "Confirm password is required.";
    } else if (password !== passwordConfirmation) {
      nextErrors.passwordConfirmation = "Passwords do not match.";
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }
    setLoading(true);
    try {
      const res = await apiRequest("api/forgot_password/reset_password", {
        method: "POST",
        body: { password, password_confirmation: passwordConfirmation },
        token,
      });
      if (res.ok) {
        setStep("success");
      } else {
        setError(
          Array.isArray(res.data?.errors)
            ? res.data.errors.join("\n")
            : res.data?.message || "Failed to reset password."
        );
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const stepTitle =
    step === "email"
      ? "Forgot Password"
      : step === "otp"
      ? "Verify OTP"
      : step === "reset"
      ? "Reset Password"
      : null;

  return (
    <div className="fixed inset-0 z-50">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/40" />

      <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
        <div className="relative w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white shadow-xl">
          {step === "success" ? (
            <div className="px-6 pb-6 pt-8">
              <div className="flex flex-col items-center text-center">
                <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full border-4 border-femty-savings-green">
                  <span className="text-3xl text-femty-savings-green">✔</span>
                </div>
                <h3 className="text-[18px] font-semibold text-femty-text md:text-[20px]">Password Reset</h3>
                <p className="mt-2 text-[14px] font-normal text-femty-muted">
                  Your password has been reset successfully. You can now sign in.
                </p>
                <button
                  type="button"
                  onClick={() => (onOpenLogin ? onOpenLogin() : onClose())}
                  className="mt-8 w-full rounded-full bg-femty-primary py-1.5 text-[14px] font-bold text-white "
                >
                  Sign In
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="relative flex items-center justify-center px-6 pt-5">
                {step === "otp" && (
                  <button
                    type="button"
                    onClick={() => setStep("email")}
                    className="absolute left-6 rounded-full p-2 text-slate-700 hover:bg-[#7352C7]/10 hover:text-femty-primary"
                    aria-label="Back"
                  >
                    ←
                  </button>
                )}
                {step === "reset" && (
                  <button
                    type="button"
                    onClick={() => setStep("otp")}
                    className="absolute left-6 rounded-full p-2 text-slate-700 hover:bg-[#7352C7]/10 hover:text-femty-primary"
                    aria-label="Back"
                  >
                    ←
                  </button>
                )}
                <p className="text-[24px] font-semibold tracking-tight text-femty-text md:text-[28px]">{stepTitle}</p>
                <button
                  type="button"
                  onClick={onClose}
                  className="absolute right-6 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E6EAF0] bg-white p-0 text-slate-500 hover:bg-[#7352C7]/10 hover:text-femty-primary"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="px-6 pb-6 pt-4">
                {step === "email" && (
                  <form onSubmit={handleSendOtp} className="space-y-4">
                    {error && (
                      <div className="whitespace-pre-line rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[14px] text-red-700">{error}</div>
                    )}
                    <div>
                      <input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full rounded-full border border-[#E6EAF0] bg-white px-4 py-1.5 text-[14px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                        placeholder="Email"
                        type="email"
                        autoComplete="email"
                      />
                      {fieldErrors.email && <p className="mt-1 text-[14px] text-red-600">{fieldErrors.email}</p>}
                    </div>
                    <p className="text-center text-[14px] font-normal text-[#6E7B86]">
                      Enter your email and we&apos;ll send you an OTP to reset your password.
                    </p>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full rounded-xl bg-femty-primary py-3 text-[14px] font-bold text-white hover:bg-femty-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading ? "Sending…" : "Send OTP"}
                    </button>
                    <div className="text-center text-[14px] font-normal text-femty-muted">
                      Remember your password?{" "}
                      <button
                        type="button"
                        onClick={() => (onOpenLogin ? onOpenLogin() : onClose())}
                        className="font-medium text-[#7352C7] "
                      >
                        Sign in
                      </button>
                    </div>
                  </form>
                )}

                {step === "otp" && (
                  <form onSubmit={handleVerifyOtp} className="space-y-4">
                    {error && (
                      <div className="whitespace-pre-line rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[14px] text-red-700">{error}</div>
                    )}
                    <p className="text-center text-[14px] font-normal text-femty-muted">Enter the 4 digit OTP sent to {email}</p>
                    <OtpInputs value={otp} onChange={setOtp} />
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full rounded-xl bg-femty-primary py-3 text-[14px] font-bold text-white hover:bg-femty-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading ? "Verifying…" : "Verify OTP"}
                    </button>
                  </form>
                )}

                {step === "reset" && (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    {error && (
                      <div className="whitespace-pre-line rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[14px] text-red-700">{error}</div>
                    )}
                    <div>
                      <div className="relative">
                        <input
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full rounded-xl border border-[#E6EAF0] bg-white px-4 py-3 pr-10 text-[14px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                          placeholder="New Password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-femty-primary"
                          aria-label={showPassword ? "Hide" : "Show"}
                        >
                          {showPassword ? <EyeIcon className="h-5 w-5" /> : <EyeOffIcon className="h-5 w-5" />}
                        </button>
                      </div>
                      {fieldErrors.password && <p className="mt-1 text-[14px] text-red-600">{fieldErrors.password}</p>}
                    </div>
                    <div>
                      <div className="relative">
                        <input
                          value={passwordConfirmation}
                          onChange={(e) => setPasswordConfirmation(e.target.value)}
                          className="w-full rounded-xl border border-[#E6EAF0] bg-white px-4 py-3 pr-10 text-[14px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                          placeholder="Confirm New Password"
                          type={showConfirm ? "text" : "password"}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirm((v) => !v)}
                          className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-femty-primary"
                          aria-label={showConfirm ? "Hide" : "Show"}
                        >
                          {showConfirm ? <EyeIcon className="h-5 w-5" /> : <EyeOffIcon className="h-5 w-5" />}
                        </button>
                      </div>
                      {fieldErrors.passwordConfirmation && (
                        <p className="mt-1 text-[14px] text-red-600">{fieldErrors.passwordConfirmation}</p>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full rounded-xl bg-femty-primary py-3 text-[14px] font-bold text-white hover:bg-femty-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loading ? "Resetting…" : "Reset Password"}
                    </button>
                  </form>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
